import { ACTION_NAMES, ActionContext, ACTIONS, Automation, FlowCall, FORM_ELEMENT_TYPES, getFlowElement, OptionGroup, selectTypes, StepType, StepTypeNames, validateWizzardFlowElement } from 'prace-common-components'
import { TemplateListItem } from 'store/api/templates/types'

export const anyActionError = (actions: Automation[], templates: TemplateListItem[]): Automation | undefined => {
  let errorAction: Automation | undefined
  actions.forEach((action: Automation) => {
    if(action.condition?.conditionalElement || action.condition?.type) {
      if((!(action.condition.conditionalElement as { id?: number })?.id && !!action.condition.type) || 
      (!!(action.condition.conditionalElement as { id?: number })?.id && !action.condition.type))
        errorAction = action
    }
    switch(action.type) {
      case ACTIONS.NOTIFY: {
        const context = action.context as ActionContext[ACTIONS.NOTIFY]
        if(!context?.notificationId) errorAction = action
        const template = templates.find((template) =>
          (template.id === context?.notificationId || template.id === context?.notification?.id))
        if(!template) errorAction = action
        break
      }
      case ACTIONS.VISIBILITY:
      case ACTIONS.ASSIGN: {
        const context = action.context as ActionContext[ACTIONS.VISIBILITY]
        if(!context?.flowElement || !context?.flowElement?.id) errorAction = action
      }
    }
  })
  return errorAction
}

export const validateEditCall = (call: FlowCall, templates: TemplateListItem[], optionGroups: OptionGroup[]) => {
  const steps = call.flow || []

  /* Count each step type across the whole call */
  const stepTypeCounts: Partial<Record<StepType, number>> = {}
  for (const step of steps) {
    const rawConfigs: any[] = (step.config as any)?.stepTypeConfigs ?? []
    for (const cfg of rawConfigs) {
      if (!cfg.stepType) continue
      const type = cfg.stepType as StepType
      stepTypeCounts[type] = (stepTypeCounts[type] || 0) + 1
    }
  }

  /* A call can only have one step of each step type */
  const duplicateStepType = (Object.keys(stepTypeCounts) as StepType[]).find((type) => (stepTypeCounts[type] || 0) > 1)
  if (duplicateStepType)
    return `A call can only have one ${StepTypeNames[duplicateStepType]} step`

  /* These step types must exist on every call */
  const REQUIRED_STEP_TYPES: StepType[] = [
    StepType.project_application,
  ]
  const missingStepType = REQUIRED_STEP_TYPES.find((type) => !stepTypeCounts[type])
  if (missingStepType)
    return `Call is missing a ${StepTypeNames[missingStepType]} step`

  /* Check if every step has assignees */
  const stepAssigneeError = steps.find((step) => !step.permissions.assignable?.length)
  if(stepAssigneeError)
    return `Step ${stepAssigneeError.title} does not have assignees`

  /* Check if every step that has assignees also has assigners (except first step) */
  const stepAssignError = steps.find(
    (step) => step.order && step.permissions.assignable?.length && !step.permissions.assign?.length)
  if(stepAssignError)
    return `Step ${stepAssignError.title} does not have assigners`

  /* Check if every step has forms */
  const stepElementsError = steps.find((step) => !step.flow?.length)
  if(stepElementsError)
    return `Step ${stepElementsError.title} does not have any form`

  /* Check if every form has elements */
  const formElementsError = steps.find((step) => !!step?.flow.find((form) => !form.flow?.length))
  if(formElementsError)
    return `Step ${formElementsError.title} has a form without elements`


  /* Check if there are  cutoffs with no start dates */
  const cutoffError = call.cutoffs.find((cutoff) => !cutoff.start)
  if(cutoffError) return 'There is a cutoff with no start date'

  /* Check if there are submission timeframes with no start and end dates */
  for (const step of steps) {
    /* Check if all step type config elements exist in the call */
    const rawConfigs: any[] = (step.config as any)?.stepTypeConfigs ?? []
    for (const cfg of rawConfigs) {
      if (cfg.elements?.length) {
        const elementIds: number[] = cfg.elements.map((e: number | { id: number }) =>
          typeof e === 'number' ? e : e?.id)
        const missingId = elementIds.find((elId) => !getFlowElement(call, elId).element)
        if (missingId !== undefined) {
          return `Step ${step.title} has a step type config that references a non-existent element`
        }
      }
    }

    if (step.timeframes && !step.order) {
      const timeframeError = step.timeframes.find((timeframe) => !timeframe.start || !timeframe.end)
      if (timeframeError)
        return `Step ${step.title} has a timeframe without start or end dates`
    }

    /* Check if there are element conditions with errors */
    for (const form of step.flow) {
      const validatedElements = validateWizzardFlowElement(form.flow, optionGroups, call)
      const elementError = validatedElements.find((el) => el.error)
      if(elementError) {
        return `Form ${form.title} has an invalid element: ${elementError.errorMsg}`
      }

      /* Check that every select field has a non-empty options group assigned (group children included) */
      const formElements = form.flow.flatMap((element) =>
        element.elementType === FORM_ELEMENT_TYPES.GROUP ? (element.flow || []) : [element])
      for(const element of formElements) {
        if(!selectTypes.includes(element.elementType as FORM_ELEMENT_TYPES)) continue
        const tag = element.optionsGroup?.tag
        if(!tag)
          return `Element ${element.title} in form ${form.title} has no options group assigned`
        /* The options list endpoint returns options for every group, so an empty list is a real misconfiguration */
        const optionGroup = optionGroups.find((group) => group.tag === tag)
        if(optionGroup && !optionGroup.options?.length)
          return `Element ${element.title} in form ${form.title} has an empty options group "${tag}"`
      }

      /* Check if all preview elements reference existing elements */
      for (const element of form.flow) {
        if(element.elementType === FORM_ELEMENT_TYPES.PREVIEW) {
          const previewIds: number[] = (element.config as any)?.previewIds || []
          const missingId = previewIds.find((id) => !getFlowElement(call, id).element)
          if(missingId !== undefined) {
            return `Element ${element.title} in form ${form.title} has an invalid preview element`
          }
        }
      }
    }
  }

  /* Check if there is any automation with an error */
  const allAutomations: Automation[] = steps.map((step) => step.automations).flat()
  const actionError = anyActionError(allAutomations, templates)
  if(actionError) {
    const step = steps.find((step) => step.id == actionError?.parentId)
    const context = actionError.context as ActionContext[ACTIONS.NOTIFY]
    if(actionError.type === ACTIONS.NOTIFY && 
      !templates.find((t) => (t.id === context?.notificationId || t.id === context?.notification?.id))) {
      return `${ACTION_NAMES[actionError.type]} automation #${actionError.id} from Step ${step?.title || '-'} has an invalid email template`
    }
    return `${ACTION_NAMES[actionError.type]} automation #${actionError.id} from Step ${step?.title || '-'} has an error`
  }

  return ''
}
